import { cn, hasMessage } from '@/lib/utils';
import { ArrowDown } from 'lucide-react';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import { useChatMessages } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';

interface Props {
  autoScrollUserMessage?: boolean;
  autoScrollAssistantMessage?: boolean;
  autoScrollRef?: MutableRefObject<boolean>;
  children: React.ReactNode;
  className?: string;
}

export default function ScrollContainer({
  autoScrollRef,
  autoScrollUserMessage,
  autoScrollAssistantMessage,
  children,
  className
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const { messages } = useChatMessages();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Two-tier update: the cheap autoscroll (one read + one write) runs
  // synchronously on every messages change so the bottom never drifts during
  // streaming. The expensive spacer recalc (sibling walk, multiple layout
  // reads) is rAF-coalesced so multiple flushes within a frame collapse to one.
  const spacerRafRef = useRef<number | null>(null);

  const autoscrollNow = useCallback(() => {
    if (!ref.current) return;
    if (!autoScrollAssistantMessage) return;
    if (!autoScrollRef?.current) return;
    ref.current.scrollTop = ref.current.scrollHeight;
  }, [autoScrollAssistantMessage, autoScrollRef]);

  const recalcSpacerImmediate = useCallback(() => {
    if (!ref.current) return;
    if (!hasMessage(messages)) return;
    if (!autoScrollUserMessage || !lastUserMessageRef.current) return;

    const containerHeight = ref.current.clientHeight;
    const lastMessageHeight = lastUserMessageRef.current.offsetHeight;

    // Sum heights of all siblings between last user message and the spacer.
    let afterMessagesHeight = 0;
    let currentElement = lastUserMessageRef.current.nextElementSibling;
    while (currentElement && currentElement !== spacerRef.current) {
      afterMessagesHeight += (currentElement as HTMLElement).offsetHeight;
      currentElement = currentElement.nextElementSibling;
    }

    const newSpacerHeight =
      containerHeight - lastMessageHeight - afterMessagesHeight - 32;
    if (spacerRef.current) {
      spacerRef.current.style.height = `${Math.max(0, newSpacerHeight)}px`;
    }

    // After resizing the spacer, position the conversation. If nothing yet
    // follows the last user message (just the spacer), pin the user message
    // at the top; otherwise let autoscroll keep the tail visible.
    if (afterMessagesHeight === 0) {
      scrollToPosition();
    } else {
      autoscrollNow();
    }
  }, [messages, autoScrollUserMessage, autoscrollNow]);

  const recalcSpacer = useCallback(() => {
    if (spacerRafRef.current !== null) return;
    spacerRafRef.current = requestAnimationFrame(() => {
      spacerRafRef.current = null;
      recalcSpacerImmediate();
    });
  }, [recalcSpacerImmediate]);

  // Track structural changes vs content-only changes. While the assistant
  // streams, only the last assistant message's content grows — no new messages
  // are added — so the user-message ref query doesn't need to re-run on every
  // flush.
  const prevMessageCountRef = useRef(0);

  // useLayoutEffect (not useEffect) so the spacer shrinks and the scroll
  // re-pins to the bottom in the same pre-paint tick as the content commit.
  // With useEffect, the browser paints once with new content but the OLD
  // spacer height — visible as a jitter (content jumps up, then on the next
  // frame the spacer recalc + autoscroll bring it back down) whenever a
  // markdown block changes height between tokens (e.g. a $...$ math fence
  // completing and KaTeX swapping in a baseline-shifted span). The rAF
  // wrapper is kept for the ResizeObserver path, which fires async after
  // paint and benefits from coalescing.
  useLayoutEffect(() => {
    if (!ref.current) return;

    if (!hasMessage(messages) && spacerRef.current) {
      spacerRef.current.style.height = `0px`;
      prevMessageCountRef.current = 0;
      return;
    }

    const structuralChange = messages.length !== prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    if (structuralChange) {
      // Re-query last user message; the DOM structure changed.
      const userMessages = ref.current.querySelectorAll(
        '[data-step-type="user_message"]'
      );
      if (userMessages.length > 0) {
        lastUserMessageRef.current = userMessages[
          userMessages.length - 1
        ] as HTMLDivElement;
      }
    }

    // Cancel any pending rAF spacer recalc — we're about to do it sync, and a
    // late rAF reading post-paint layout would just redo the same write.
    if (spacerRafRef.current !== null) {
      cancelAnimationFrame(spacerRafRef.current);
      spacerRafRef.current = null;
    }

    if (lastUserMessageRef.current) {
      recalcSpacerImmediate();
    } else {
      autoscrollNow();
    }
  }, [messages, autoscrollNow, recalcSpacerImmediate]);

  // Cancel any pending rAF on unmount.
  useEffect(() => {
    return () => {
      if (spacerRafRef.current !== null) {
        cancelAnimationFrame(spacerRafRef.current);
        spacerRafRef.current = null;
      }
    };
  }, []);

  // Add window resize listener to update spacer height
  useEffect(() => {
    if (!autoScrollUserMessage) return;

    const handleResize = () => {
      recalcSpacer();
    };

    window.addEventListener('resize', handleResize);

    // Initial update
    recalcSpacer();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoScrollUserMessage, recalcSpacer]);

  // Observe content-size changes inside the scroll container so the spacer
  // shrinks when an asynchronously-rendered child (Plotly chart, lazy image,
  // etc.) grows after its message first appeared. Without this, the spacer
  // is sized when the message first lands — before the chart has loaded —
  // leaving phantom blank space below until a window resize triggers recalc.
  useEffect(() => {
    if (!ref.current || !autoScrollUserMessage) return;
    const observer = new ResizeObserver(() => recalcSpacer());
    for (const child of ref.current.children) {
      if (child !== spacerRef.current) observer.observe(child);
    }
    return () => observer.disconnect();
  }, [autoScrollUserMessage, recalcSpacer]);

  // Check scroll position on mount
  useEffect(() => {
    if (!ref.current) return;

    setTimeout(() => {
      if (!ref.current) return;

      const { scrollTop, scrollHeight, clientHeight } = ref.current;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setShowScrollButton(!atBottom);
    }, 500);
  }, []);

  const checkScrollEnd = () => {
    if (!ref.current) return;

    const prevScrollTop = ref.current.scrollTop;

    setTimeout(() => {
      if (!ref.current) return;

      const currentScrollTop = ref.current.scrollTop;
      if (currentScrollTop === prevScrollTop) {
        setIsScrolling(false);

        const { scrollTop, scrollHeight, clientHeight } = ref.current;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setShowScrollButton(!atBottom);
      } else {
        checkScrollEnd();
      }
    }, 100);
  };

  const scrollToBottom = () => {
    if (!ref.current) return;

    setIsScrolling(true);
    ref.current.scrollTo({
      top: ref.current.scrollHeight,
      behavior: 'smooth'
    });

    if (autoScrollRef) {
      autoScrollRef.current = true;
    }

    setShowScrollButton(false);
    checkScrollEnd();
  };

  const scrollToPosition = () => {
    if (!ref.current || !lastUserMessageRef.current) return;

    setIsScrolling(true);
    // Scroll to position the last user message at the top with some padding
    const scrollPosition = lastUserMessageRef.current.offsetTop - 20;

    ref.current.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });

    setShowScrollButton(false);
    checkScrollEnd();
  };

  const handleScroll = () => {
    if (!ref.current || isScrolling) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (autoScrollRef) {
      autoScrollRef.current = atBottom;
    }

    setShowScrollButton(!atBottom);
  };

  return (
    <div className="relative flex flex-col flex-grow overflow-y-auto">
      <div
        ref={ref}
        className={cn('flex flex-col flex-grow overflow-y-auto', className)}
        onScroll={handleScroll}
      >
        {children}
        {/* Dynamic spacer to position the last user message at the top */}
        <div ref={spacerRef} className="flex-shrink-0" />
      </div>

      {showScrollButton ? (
        <div className="absolute bottom-4 left-0 right-0 flex justify-center">
          <Button
            size="icon"
            variant="outline"
            className="rounded-full"
            onClick={scrollToBottom}
          >
            <ArrowDown className="size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
